import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { httpService } from "../../services/httpService";
import DynamicTable from "../common/DynamicTable";
import SupplierReturn from "./SupplierReturn";
import { IoClose } from "react-icons/io5"; // for close button
import { HiArrowSmLeft } from "react-icons/hi";

const Supplierexpiry = () => {
  const { supplierId } = useParams();
  const [returns, setReturns] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedReturn, setSelectedReturn] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showReturnDrawer, setShowReturnDrawer] = useState(false);

  const [returnDetails, setReturnDetails] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Fetch returns
  useEffect(() => {
    if (!supplierId) return;
    setLoading(true);
    httpService
      .get(`/vendorsupplierreturn/getVendorSupplierReturns/${supplierId}`)
      .then((res) => {
        if (res.status && res.code === 200) {
          setReturns(res.data || []);
        } else {
          setReturns([]);
          setError("No return data found");
        }
      })
      .catch(() => setError("Failed to fetch data"))
      .finally(() => setLoading(false));
  }, [supplierId]);

  // Fetch products
  useEffect(() => {
    if (!supplierId) return;
    httpService
      .get(`/vendorsupplier/getVendorSupplierProducts/${supplierId}`)
      .then((res) => {
        if (res.status && res.code === 200) {
          setProducts(res.data || []);
        }
      })
      .catch(() => setProducts([]));
  }, [supplierId]);

  useEffect(() => {
    if (!showDrawer || !selectedReturn) return;
    setDetailsLoading(true);
    httpService
      .get(`/vendorsupplierreturn/getVendorSupplierReturnDetails/${selectedReturn._id}`)
      .then((res) => {
        if (res.status && res.code === 200) {
          setReturnDetails(res.data || []);
        } else {
          setReturnDetails([]);
        }
      })
      .catch(() => setReturnDetails([]))
      .finally(() => setDetailsLoading(false));
  }, [showDrawer, selectedReturn]);

  const columns = [
  {
    key: "createdAt",
    header: "Return Date",
    renderCell: (row) => (
      <button 
        className="text-blue-600 underline"
        onClick={() => {
          setSelectedReturn(row);
          setShowDrawer(true);
        }}
      >
        {new Date(row.createdAt).toLocaleDateString('en-GB')} {/* DD-MM-YYYY format */}
      </button>
    )
  },
  {
    key: "num_products",
    header: "# of Products",
    renderCell: (row) => row.items?.length || 0
  },
  {
    key: "base_amount",
    header: "total Amount",
    renderCell: (row) => {
      const base = row.items?.reduce((sum, item) => 
        sum + (item.purchase_price * item.return_quantity), 0) || 0;
      return `₹${base.toFixed(2)}`;
    }
  },
  {
    key: "total_gst",
    header: "Total GST",
    renderCell: (row) => {
      const gst = row.items?.reduce((sum, item) =>
        sum + (item.purchase_price * item.return_quantity * item.gst_amount / 100), 0) || 0;
      return `₹${gst.toFixed(2)}`;
    }
  },
  {
    key: "return_amount", 
    header: "Return Amount",
    renderCell: (row) => {
      const base = row.items?.reduce((sum, item) => 
        sum + (item.purchase_price * item.return_quantity), 0) || 0;
      const gst = row.items?.reduce((sum, item) =>
        sum + (item.purchase_price * item.return_quantity * item.gst_amount / 100), 0) || 0;
      return `₹${(base + gst).toFixed(2)}`;
    }
  }
];

  const tableData = returns.map((entry) => {
    return {
      ...entry,
      num_products: entry.items ? entry.items.length : 0,
    };
  });

  const refreshReturns = async () => {
    setLoading(true);
    
    try {
      const res = await httpService.get(`/vendorsupplierreturn/getVendorSupplierReturns/${supplierId}`);
      
      if (res.status && res.code === 200) {
        setReturns(res.data || []);
        
        // If details drawer is open, find and update the selectedReturn
        if (showDrawer && selectedReturn) {
          const updatedReturn = res.data.find(r => r._id === selectedReturn._id);
          if (updatedReturn) {
            setSelectedReturn(updatedReturn);
            // Refetch details with updated return
            setDetailsLoading(true);
            try {
              const detailsRes = await httpService.get(`/vendorsupplierreturn/getVendorSupplierReturnDetails/${updatedReturn._id}`);
              if (detailsRes.status && detailsRes.code === 200) {
                setReturnDetails(detailsRes.data || []);
              } else {
                setReturnDetails([]);
              }
            } catch (error) {
              setReturnDetails([]);
            } finally {
              setDetailsLoading(false);
            }
          }
        }
      } else {
        setReturns([]);
        setError("No return data found");
      }
    } catch (error) {
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showReturnDrawer) {
      // Drawer just closed, refresh returns
      refreshReturns();
    }
    // eslint-disable-next-line
  }, [showReturnDrawer]);

  useEffect(() => {
    if (!showDrawer) {
      // Details drawer just closed, refresh returns
      refreshReturns();
    }
    // eslint-disable-next-line
  }, [showDrawer]);

  return (
    <div className="bg-white p-6 rounded-xl shadow">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-slate-700">Supplier Returns</h3>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-full shadow"
          onClick={() => setShowReturnDrawer(true)}
        >
          + Add Return
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : (
        <DynamicTable columns={columns} data={tableData} />
      )}

      {/* Add SupplierReturn drawer */}
      <SupplierReturn
        open={showReturnDrawer}
        onClose={() => setShowReturnDrawer(false)}
        supplierId={supplierId}
        onReturnSaved={refreshReturns}
      />

      {/* Existing details drawer for viewing a return */}
      {showDrawer && selectedReturn && (
        <>
          <div className="fixed top-0 right-0 h-full w-[500px] bg-white shadow-lg z-50 transform transition-transform duration-300">
            {/* Header */}
            <div className="flex items-center gap-2 p-4 bg-white">
              <button onClick={() => setShowDrawer(false)} className="text-blue-900 hover:bg-gray-100 rounded-full p-1 mr-2">
                <HiArrowSmLeft className="w-7 h-7" />
              </button>
              <h2 className="text-2xl font-bold text-blue-900">Supplier Return</h2>
            </div>
            <div className="border-b border-gray-200" />
            <div className="px-4 pt-2 pb-4 text-gray-600 text-sm">
              Return Details – {new Date(selectedReturn.createdAt).toLocaleDateString('en-GB')}
            </div>

            {/* Table */}
            <div className="p-4 overflow-y-auto h-[calc(100%-60px)]">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-300 text-left">
                    <th className="p-2 border">SKU Number</th>
                    <th className="p-2 border">Return Qty</th>
                    <th className="p-2 border">Price</th>
                    <th className="p-2 border">GST</th>
                    <th className="p-2 border">Return Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {detailsLoading ? (
                    <tr>
                      <td colSpan={5} className="text-center">Loading...</td>
                    </tr>
                  ) : (
                    returnDetails.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="p-2 border">{item.sku_number}</td>
                        <td className="p-2 border">{item.return_quantity}</td>
                        <td className="p-2 border">₹{item.purchase_price}</td>
                        <td className="p-2 border">{item.gst_amount}%</td>
                        <td className="p-2 border">₹{item.return_amount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Supplierexpiry;